import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  addVariationOption,
  createVariationType,
  deleteVariationOption,
  deleteVariationType,
  getMerchantMe,
  listVariationTypes,
  updateVariationOption,
  updateVariationType,
  type VariationOption,
  type VariationType,
} from '../lib/api';

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

/**
 * Merchant Variation Types. The admin's GLOBAL types (Size, Color…) show here
 * read-only — use them for consistency. A merchant can also create their OWN
 * types for niche axes; those are private to their products.
 */
export function VariationTypesPage() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['merchant-me'], queryFn: getMerchantMe });
  const approved = me?.merchant.status === 'approved';
  const query = useQuery({
    queryKey: ['merchant-variation-types'],
    queryFn: () => listVariationTypes(true),
    enabled: approved,
  });
  const [creating, setCreating] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['merchant-variation-types'] });
  const create = useMutation({
    meta: { successMessage: 'Variation type created.' },
    mutationFn: createVariationType,
    onSuccess: () => {
      invalidate();
      setCreating(false);
    },
  });

  const types = query.data ?? [];
  const global = types.filter((t) => t.ownerMerchantId === null);
  const own = types.filter((t) => t.ownerMerchantId !== null);

  // Gated identically to Products — variation types feed product submission,
  // which is admin-approval-only.
  if (me && !approved) {
    return (
      <div className="max-w-3xl space-y-4">
        <h1 className="text-2xl font-semibold">Variation Types</h1>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Your merchant account is{' '}
            <span className="font-medium capitalize">{me.merchant.status}</span>. Variation types
            become available once an admin approves your account.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Variation Types</h1>
        <Button onClick={() => setCreating(true)}>+ New type</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Use the store's shared types where they fit (keeps your listings consistent with the rest of
        the catalog). Create your own only for axes the shared set doesn't cover.
      </p>

      {creating && (
        <TypeForm
          submitting={create.isPending}
          error={create.error}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => setCreating(false)}
        />
      )}

      <h2 className="text-sm font-semibold text-muted-foreground">Your types</h2>
      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : own.length === 0 ? (
        <p className="text-sm text-muted-foreground">You haven't created any yet.</p>
      ) : (
        own.map((t) => <OwnTypeCard key={t.id} type={t} onChanged={invalidate} />)
      )}

      <h2 className="pt-2 text-sm font-semibold text-muted-foreground">Shared types (read-only)</h2>
      {global.length === 0 ? (
        <p className="text-sm text-muted-foreground">None defined by the store.</p>
      ) : (
        global.map((t) => (
          <Card key={t.id} className="opacity-90">
            <CardHeader className="py-3">
              <CardTitle className="flex items-center gap-2 text-base">
                {t.name}
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {t.code}
                </span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  shared
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-1.5 pb-3">
              {t.options.map((o) => (
                <span key={o.id} className="rounded-full border px-2 py-0.5 text-xs">
                  {o.label}
                </span>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function OwnTypeCard({ type, onChanged }: { type: VariationType; onChanged: () => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['merchant-variation-types'] });
    onChanged();
  };
  const update = useMutation({
    meta: { successMessage: 'Variation type updated.' },
    mutationFn: (patch: Partial<{ name: string; sortOrder: number; active: boolean }>) =>
      updateVariationType(type.id, patch),
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });
  const remove = useMutation({
    meta: { successMessage: 'Variation type deleted.' },
    mutationFn: () => deleteVariationType(type.id),
    onSuccess: invalidate,
  });

  return (
    <Card className={type.active ? '' : 'opacity-60'}>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 py-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {type.name}
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
            {type.code}
          </span>
          {!type.active && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              inactive
            </span>
          )}
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => update.mutate({ active: !type.active })}
          >
            {type.active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => remove.mutate()}>
            Delete
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pb-3">
        {editing && (
          <TypeForm
            type={type}
            submitting={update.isPending}
            error={update.error}
            onSubmit={(input) => update.mutate({ name: input.name, sortOrder: input.sortOrder })}
            onCancel={() => setEditing(false)}
          />
        )}
        {errText(remove.error) && (
          <p className="text-xs text-destructive">{errText(remove.error)}</p>
        )}
        <OptionsEditor type={type} onChanged={onChanged} />
      </CardContent>
    </Card>
  );
}

function OptionsEditor({ type, onChanged }: { type: VariationType; onChanged: () => void }) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['merchant-variation-types'] });
    onChanged();
  };
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const add = useMutation({
    meta: { successMessage: 'Option added.' },
    mutationFn: () => addVariationOption(type.id, { code: code.trim(), label: label.trim() }),
    onSuccess: () => {
      setCode('');
      setLabel('');
      invalidate();
    },
  });
  const toggle = useMutation({
    meta: { successMessage: 'Option updated.' },
    mutationFn: (o: VariationOption) => updateVariationOption(o.id, { active: !o.active }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    meta: { successMessage: 'Option deleted.' },
    mutationFn: (id: string) => deleteVariationOption(id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {type.options.length === 0 && (
          <span className="text-xs text-muted-foreground">No values yet.</span>
        )}
        {type.options.map((o) => (
          <span
            key={o.id}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${o.active ? '' : 'opacity-50'}`}
          >
            {o.label}
            <span className="font-mono text-[10px] text-muted-foreground">{o.code}</span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => toggle.mutate(o)}
            >
              {o.active ? '⦸' : '✓'}
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => remove.mutate(o.id)}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <div className="space-y-1">
          <label className="text-[10px] text-muted-foreground">Value label</label>
          <Input
            className="h-8 w-32"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Small"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] text-muted-foreground">Code</label>
          <Input
            className="h-8 w-24"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="S"
          />
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!code.trim() || !label.trim() || add.isPending}
          onClick={() => add.mutate()}
        >
          + Add value
        </Button>
      </div>
      {errText(add.error) && <p className="text-xs text-destructive">{errText(add.error)}</p>}
      {errText(remove.error) && <p className="text-xs text-destructive">{errText(remove.error)}</p>}
    </div>
  );
}

function TypeForm({
  type,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  type?: VariationType;
  submitting: boolean;
  error: unknown;
  onSubmit: (input: { code: string; name: string; sortOrder: number }) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(type?.code ?? '');
  const [name, setName] = useState(type?.name ?? '');
  const [sortOrder, setSortOrder] = useState(String(type?.sortOrder ?? 0));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          code: code.toUpperCase(),
          name: name.trim(),
          sortOrder: Number(sortOrder) || 0,
        });
      }}
      className="grid grid-cols-1 gap-3 rounded-md border bg-muted/30 p-3 md:grid-cols-3"
    >
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Code</label>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          disabled={!!type}
          placeholder="FIT, SCENT"
          required
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Fit" required />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Sort order</label>
        <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </div>
      {errText(error) && <p className="text-sm text-destructive md:col-span-3">{errText(error)}</p>}
      <div className="flex gap-2 md:col-span-3">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : type ? 'Save' : 'Create'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
